import FileLoaderService from './fileLoaderService';
import { DicomMetadataStore } from '@ohif/core';

// Local-study-open audit log (RIS build plan §11.3) — best-effort only.
// This frame never holds a credential for radiology-service's API (see
// the M360 fork README for why: it's embedded via iframe with no
// channel to the host's httpOnly session token, by design — a raw
// token was deliberately never introduced here). Instead it computes
// the file's own hash and asks its *parent* window to record the
// audit entry using the parent's own already-authenticated session.
// postMessage's targetOrigin is '*' here on purpose: this payload
// carries no credential and no PHI (a DICOM UID + a hash + a
// filename), so there's nothing sensitive to protect in transit — the
// real trust boundary is on the receiving end, where the parent must
// verify event.origin before acting on it (see web-client's viewer
// page). If nothing is listening (e.g. this viewer opened standalone,
// outside the M360 iframe), the message is simply never picked up —
// local loading itself is completely unaffected either way.
async function sha256Hex(file) {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
}

function reportLocalStudyOpened(file, dataset) {
  if (!dataset.StudyInstanceUID || typeof window === 'undefined' || window.parent === window) {
    return;
  }
  sha256Hex(file)
    .then(fileHashSha256 => {
      window.parent.postMessage(
        {
          source: 'm360-radiology-viewer',
          type: 'local-study-opened',
          payload: {
            study_instance_uid: dataset.StudyInstanceUID,
            sop_instance_uid: dataset.SOPInstanceUID || '',
            file_hash_sha256: fileHashSha256,
            file_name: file.name || '',
          },
        },
        '*'
      );
    })
    .catch(hashError => {
      console.warn('Could not compute local-study audit hash:', hashError.message);
    });
}

const processFile = async file => {
  try {
    const fileLoaderService = new FileLoaderService(file);
    const imageId = fileLoaderService.addFile(file);
    const image = await fileLoaderService.loadFile(file, imageId);
    const dicomJSONDataset = await fileLoaderService.getDataset(image, imageId);

    DicomMetadataStore.addInstance(dicomJSONDataset);
    reportLocalStudyOpened(file, dicomJSONDataset);
  } catch (error) {
    console.log(error.name, ':Error when trying to load and process local files:', error.message);
  }
};

export default async function filesToStudies(files) {
  const processFilesPromises = files.map(processFile);
  await Promise.all(processFilesPromises);

  return DicomMetadataStore.getStudyInstanceUIDs();
}
